import React, { useState } from 'react';
import {
  CharacterPortrait,
  ChapterEndScene,
  ChoiceOption,
  ChoiceScene,
  GameOverScene,
  RecordCardScene,
  PhoneMailInteractScene,
  PhoneMailScene,
  Scenario,
  Scene,
  TextOverlayScene,
  DialogueScene,
  ObservationScene,
  ClickableScreenScene,
  SituationSortGameScene,
} from '../../types';
import { InlineEditableText } from './InlineEditableText';
import { InlineDialogueMeta } from './InlineDialogueMeta';
import { InlineStagePanel } from './InlineStagePanel';
import { mergeSceneStagePatch } from '../../systems/stage-runtime';
import { InlineChoiceEditor, InlineChoiceReadonly } from './InlineChoiceEditor';
import { InlineChapterEndEditor } from './InlineChapterEndEditor';
import { InlineGameOverEditor } from './InlineGameOverEditor';
import { InlineRecordCardEditor } from './InlineRecordCardEditor';
import { InlineTextOverlayEditor } from './InlineTextOverlayEditor';
import { InlinePhoneMailEditor, getPhoneMailTypeLabel } from './InlinePhoneMailEditor';
import {
  InlinePhoneConversationEditor,
  InlinePhoneConversationReadonly,
} from './InlinePhoneConversationEditor';
import { getContentItemLabel, getContentItemSummary } from './utils';
import styles from './inline-editor.module.css';
import { InlineLineControls } from './InlineLineControls';
import { InlineInsertSlot, InlineInsertForm, InlineInsertProvider } from './inline-insert-context';
import { useInlineClipboard } from './inline-clipboard-context';
import { BRANCH_INSERT_TYPES, buildInsertItem } from './inline-insert-shared';
import { uniqueId } from './utils';
import { InlineBlockBackgroundControls } from './InlineBlockBackgroundControls';
import { InlineElementsStrip } from './InlineElementsStrip';
import type { OpenSceneInEditorHandler } from './inline-elements-model';
import { InlineSituationSortGameEditor } from './InlineSituationSortGameEditor';
import { InlineSceneList } from './InlineSceneList';
import type { SortCategoryActions } from './sort-category-actions';
import type { BackgroundSectionToggleProps } from './InlineBackgroundPanel';
import type { InlineScenarioChangeOptions } from './use-inline-block-history';

interface InlineContentLineProps {
  blockId: string;
  item: Scene;
  flatIndex: number;
  sceneId: string;
  contentIndex: number;
  editMode: boolean;
  characters: CharacterPortrait[];
  sortCategoryActions?: SortCategoryActions;
  flatBlocks: Array<{ blockId: string; item: Scene; sceneId: string; contentIndex: number; flatIndex: number }>;
  scenarioId: string;
  scenario: Scenario;
  inBackgroundSection?: boolean;
  backgroundSectionToggle?: BackgroundSectionToggleProps;
  onChange: (flatIndex: number, item: Scene, options?: InlineScenarioChangeOptions) => void;
  onChoiceBlockLink?: (
    flatIndex: number,
    optionIndex: number,
    blockId: string | undefined
  ) => void;
  onRemove: (flatIndex: number) => void;
  onMove: (flatIndex: number, dir: -1 | 1) => void;
  onOpenSceneInEditor?: OpenSceneInEditorHandler;
  onPreviewBlock?: (sceneId: string) => void;
  isFirstInChapter?: boolean;
}

interface ChoiceBranchPanelProps {
  branchScenes: Scene[];
  scenarioId: string;
  scenario: Scenario;
  editMode: boolean;
  characters: CharacterPortrait[];
  sortCategoryActions?: SortCategoryActions;
  onBranchChange: (scenes: Scene[], options?: InlineScenarioChangeOptions) => void;
  onOpenSceneInEditor?: OpenSceneInEditorHandler;
  onPreviewBlock?: (sceneId: string) => void;
}

function ChoiceBranchPanel({
  branchScenes,
  scenarioId,
  scenario,
  editMode,
  characters,
  sortCategoryActions,
  onBranchChange,
  onOpenSceneInEditor,
  onPreviewBlock,
}: ChoiceBranchPanelProps) {
  const [insertOpen, setInsertOpen] = useState(false);
  const clipboard = useInlineClipboard();

  const insertBranchSceneAfter = (
    afterSceneId: string | null,
    type: string,
    variant?: string,
    text?: string
  ) => {
    const body = buildInsertItem(type, variant, text);
    insertBranchSceneBody(afterSceneId, body);
    setInsertOpen(false);
  };

  const insertBranchSceneBody = (afterSceneId: string | null, body: Omit<Scene, 'id'>) => {
    const scene = { ...body, id: uniqueId('scene') } as Scene;
    const next = [...branchScenes];
    if (afterSceneId === null) {
      next.unshift(scene);
    } else {
      const idx = branchScenes.findIndex((s) => s.id === afterSceneId);
      next.splice(idx >= 0 ? idx + 1 : next.length, 0, scene);
    }
    onBranchChange(next, { recordBlockHistory: true });
    setInsertOpen(false);
  };

  const addBranchSceneAtEnd = (type: string, variant?: string, text?: string) => {
    const afterId = branchScenes.length > 0 ? branchScenes[branchScenes.length - 1]!.id : null;
    insertBranchSceneAfter(afterId, type, variant, text);
  };

  const pasteBranchSceneAtEnd = (body: Omit<Scene, 'id'>) => {
    const afterId = branchScenes.length > 0 ? branchScenes[branchScenes.length - 1]!.id : null;
    insertBranchSceneBody(afterId, body);
  };

  return (
    <div className={styles.branchPanel}>
      <InlineInsertProvider
        insertTypes={BRANCH_INSERT_TYPES}
        onInsertAfter={insertBranchSceneAfter}
        onInsertPasted={insertBranchSceneBody}
      >
        <div className={styles.branchPanelBody}>
          {branchScenes.length === 0 && (
            <div className={styles.branchPanelEmpty}>Réponse directe (aucune ligne dédiée)</div>
          )}
          <InlineSceneList
            scenes={branchScenes}
            flatBlocks={branchScenes.map((branchScene, branchIndex) => ({
              blockId: branchScene.id,
              item: branchScene,
              sceneId: branchScene.id,
              contentIndex: 0,
              flatIndex: branchIndex,
            }))}
            scenarioId={scenarioId}
            scenario={scenario}
            editMode={editMode}
            characters={characters}
            sortCategoryActions={sortCategoryActions}
            onChange={(idx, next, options) =>
              onBranchChange(
                branchScenes.map((s, i) => (i === idx ? { ...next, id: s.id } : s)),
                options
              )
            }
            onRemove={(idx) =>
              onBranchChange(branchScenes.filter((_, i) => i !== idx), {
                recordBlockHistory: true,
              })
            }
            onMove={(idx, dir) => {
              const target = idx + dir;
              if (target < 0 || target >= branchScenes.length) return;
              const next = [...branchScenes];
              const [moved] = next.splice(idx, 1);
              next.splice(target, 0, moved!);
              onBranchChange(next, { recordBlockHistory: true });
            }}
            onOpenSceneInEditor={onOpenSceneInEditor}
            onPreviewBlock={onPreviewBlock}
          />
        </div>
        {editMode && (
          <div className={styles.branchPanelActions}>
            {!insertOpen ? (
              <button
                type="button"
                className={styles.branchAddBtn ?? ''}
                onClick={() => setInsertOpen(true)}
              >
                + Ligne dans la branche
              </button>
            ) : (
              <InlineInsertForm
                compact
                insertTypes={BRANCH_INSERT_TYPES}
                onAdd={addBranchSceneAtEnd}
                onInsertPasted={() => {
                  const body = clipboard?.getPasteBody();
                  if (body) pasteBranchSceneAtEnd(body);
                }}
                onCancel={() => setInsertOpen(false)}
              />
            )}
          </div>
        )}
      </InlineInsertProvider>
    </div>
  );
}

function charName(characters: CharacterPortrait[], characterId?: string, speaker?: string): string {
  if (characterId) {
    return characters.find((c) => c.id === characterId)?.name ?? characterId;
  }
  return speaker || 'Personnage';
}


function wrapBlock(
  blockId: string,
  editMode: boolean,
  node: React.ReactNode,
  leading?: React.ReactNode,
  inBackgroundSection?: boolean,
  navSceneId?: string,
  overlay?: {
    sceneId: string;
    elements?: Scene['elements'];
    onOpenSceneInEditor?: OpenSceneInEditorHandler;
  }
) {
  return (
    <div
      id={navSceneId ? `inline-scene-${navSceneId}` : undefined}
      className={`${styles.sceneBlock}${inBackgroundSection ? ` ${styles.bgSectionMember}` : ''}`}
    >
      {leading}
      {node}
      {overlay?.elements && overlay.elements.length > 0 && (
        <InlineElementsStrip
          sceneId={overlay.sceneId}
          elements={overlay.elements}
          onOpenSceneInEditor={overlay.onOpenSceneInEditor}
        />
      )}
      {editMode && <InlineInsertSlot blockId={blockId} />}
    </div>
  );
}

function BlockTypeLabel({
  label,
  sceneId,
  isFirstInChapter = false,
}: {
  label: string;
  sceneId: string;
  isFirstInChapter?: boolean;
}) {
  return (
    <span className={styles.slLabel}>
      {label}
      {isFirstInChapter && <span className={styles.slSceneId}> ({sceneId})</span>}
    </span>
  );
}

export const InlineContentLine: React.FC<InlineContentLineProps> = ({
  blockId,
  item,
  flatIndex,
  editMode,
  characters,
  sortCategoryActions,
  scenarioId,
  scenario,
  backgroundSectionToggle,
  onChange,
  onRemove,
  onMove,
  onOpenSceneInEditor,
  onPreviewBlock,
  inBackgroundSection = false,
  sceneId,
  isFirstInChapter = false,
}) => {
  const controls = onPreviewBlock || editMode || onOpenSceneInEditor ? (
    <InlineLineControls
      blockId={blockId}
      scene={item}
      editMode={editMode}
      onOpenInEditor={onOpenSceneInEditor ? () => onOpenSceneInEditor(sceneId) : undefined}
      onPreview={onPreviewBlock ? () => onPreviewBlock(sceneId) : undefined}
      onMoveUp={() => onMove(flatIndex, -1)}
      onMoveDown={() => onMove(flatIndex, 1)}
      onDelete={() => onRemove(flatIndex)}
    />
  ) : null;

  const patchScene = (next: Scene, options?: InlineScenarioChangeOptions) =>
    onChange(flatIndex, { ...next, id: sceneId }, options);

  const bgControls = (
    <InlineBlockBackgroundControls
      scenarioId={scenarioId}
      scene={item}
      editMode={editMode}
      onChange={patchScene}
      sectionToggle={backgroundSectionToggle}
    />
  );

  const overlayContext =
    item.elements && item.elements.length > 0
      ? { sceneId, elements: item.elements, onOpenSceneInEditor }
      : undefined;

  if (item.type === 'dialogue') {
    const d = item as DialogueScene;
    const isInternal = d.isInternal === true;
    const name = charName(characters, d.characterId, d.speaker);

    const patchDialogue = (patch: Partial<DialogueScene>) =>
      onChange(flatIndex, mergeSceneStagePatch(d, patch));

    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${isInternal ? styles.slMonologue : styles.slVoice}`}>
        {controls}
        <BlockTypeLabel
          label={isInternal ? `Monologue — ${name}` : 'Dialogue'}
          sceneId={sceneId}
          isFirstInChapter={isFirstInChapter}
        />
        <InlineDialogueMeta
          item={d}
          characters={characters}
          editMode={editMode}
          onChange={patchDialogue}
        />
        <InlineEditableText
          value={d.text}
          editMode={editMode}
          className={styles.slText}
          onChange={(text) => patchDialogue({ text })}
          placeholder="Texte du dialogue…"
        />
        {editMode && isInternal && (
          <InlineStagePanel
            scene={d}
            scenario={scenario}
            characters={characters}
            editMode={editMode}
            allowAdd
            onChange={(patch) => patchDialogue(patch as Partial<DialogueScene>)}
          />
        )}
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'choice') {
    const c = item as ChoiceScene;

    const patchOption = (optionIndex: number, patch: Partial<ChoiceOption>, options?: InlineScenarioChangeOptions) => {
      const nextOptions = c.options.map((o, i) => (i === optionIndex ? { ...o, ...patch } : o));
      onChange(flatIndex, { ...c, options: nextOptions }, options);
    };

    const renderBranch = (optionIndex: number) => {
      const branchScenes = c.options[optionIndex]?.scenes ?? [];

      return (
        <ChoiceBranchPanel
          branchScenes={branchScenes}
          scenarioId={scenarioId}
          scenario={scenario}
          editMode={editMode}
          characters={characters}
          sortCategoryActions={sortCategoryActions}
          onBranchChange={(scenes, options) =>
            patchOption(optionIndex, { scenes: scenes.length ? scenes : undefined }, options)
          }
          onOpenSceneInEditor={onOpenSceneInEditor}
          onPreviewBlock={onPreviewBlock}
        />
      );
    };

    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slChoice}`}>
        {controls}
        <BlockTypeLabel label="Choix" sceneId={sceneId} isFirstInChapter={isFirstInChapter} />
        {(c.prompt || editMode) && (
          <InlineEditableText
            value={c.prompt ?? ''}
            editMode={editMode}
            className={styles.slText}
            onChange={(prompt) => onChange(flatIndex, { ...c, prompt })}
            placeholder="Intitulé du choix (optionnel)…"
          />
        )}
        <InlineStagePanel
          scene={c}
          scenario={scenario}
          characters={characters}
          editMode={editMode}
          allowAdd
          onChange={(patch) => onChange(flatIndex, mergeSceneStagePatch(c, patch))}
        />
        {editMode ? (
          <InlineChoiceEditor
            item={c}
            characters={characters}
            onChange={(next, options) => onChange(flatIndex, next, options)}
            renderBranch={renderBranch}
          />
        ) : (
          <InlineChoiceReadonly
            options={c.options}
            characters={characters}
            linkLabels={c.options.map((o) => {
              if (o.goto) return o.goto;
              const last = o.scenes?.[o.scenes.length - 1];
              if (last?.type === 'gameOver') {
                return last.goto ? `game over → ${last.goto}` : 'game over';
              }
              return o.scenes?.length ? `${o.scenes.length} ligne(s)` : '';
            })}
          />
        )}
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'phoneCall' || item.type === 'phoneChat') {
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slPhone}`}>
        {controls}
        <BlockTypeLabel
          label={`${item.type === 'phoneChat' ? 'SMS' : 'Appel'} — ${item.contactName || 'Contact'}`}
          sceneId={sceneId}
          isFirstInChapter={isFirstInChapter}
        />
        {editMode ? (
          <InlinePhoneConversationEditor
            item={item}
            scenarioId={scenarioId}
            onChange={(next, options) => onChange(flatIndex, { ...item, ...next }, options)}
          />
        ) : (
          <InlinePhoneConversationReadonly item={item} />
        )}
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'phoneMail' || item.type === 'phoneMailInteract') {
    const m = item as PhoneMailScene | PhoneMailInteractScene;
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slPhone}`}>
        {controls}
        <BlockTypeLabel
          label={getPhoneMailTypeLabel(m)}
          sceneId={sceneId}
          isFirstInChapter={isFirstInChapter}
        />
        <InlinePhoneMailEditor
          item={m}
          editMode={editMode}
          onChange={(next) => onChange(flatIndex, next)}
        />
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'command') {
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slNote}`}>
        {controls}
        <BlockTypeLabel label="Commande" sceneId={sceneId} isFirstInChapter={isFirstInChapter} />
        <span className={styles.slText}>
          {item.command} → {item.target}
          {item.value !== undefined ? ` = ${JSON.stringify(item.value)}` : ''}
        </span>
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'textOverlay') {
    const t = item as TextOverlayScene;
    if (t.overlayType === 'chapterEnd') {
      return wrapBlock(
        blockId,
        editMode,
        <div className={`${styles.sl} ${styles.slBlock}`}>
          {controls}
          <BlockTypeLabel
            label="Fin de chapitre (legacy)"
            sceneId={sceneId}
            isFirstInChapter={isFirstInChapter}
          />
          <span className={styles.slText}>{t.title || t.content || '…'}</span>
        </div>,
        bgControls,
        inBackgroundSection,
        overlayContext
      );
    }
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slBlock}`}>
        {controls}
        <BlockTypeLabel
          label="Overlay"
          sceneId={sceneId}
          isFirstInChapter={isFirstInChapter}
        />
        <InlineTextOverlayEditor
          item={t}
          editMode={editMode}
          scenarioId={scenarioId}
          characters={characters}
          onChange={(next) => onChange(flatIndex, next)}
        />
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'timedOverlay') {
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slTransition}`}>
        {controls}
        <BlockTypeLabel label="Transition" sceneId={sceneId} isFirstInChapter={isFirstInChapter} />
        <InlineEditableText
          value={item.message}
          editMode={editMode}
          className={styles.slText}
          onChange={(message) => onChange(flatIndex, { ...item, message })}
        />
        {item.goto && <span className={styles.choiceDest}>→ {item.goto}</span>}
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'chapterEnd') {
    const chapterEnd = item as ChapterEndScene;
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slChapterEnd}`}>
        {controls}
        <BlockTypeLabel
          label="Fin de chapitre"
          sceneId={sceneId}
          isFirstInChapter={isFirstInChapter}
        />
        <InlineChapterEndEditor
          item={chapterEnd}
          editMode={editMode}
          sceneIds={scenario.scenes.map((s) => s.id)}
          onChange={(next, options) => onChange(flatIndex, next, options)}
        />
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'gameOver') {
    const gameOver = item as GameOverScene;
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slGameOver}`}>
        {controls}
        <BlockTypeLabel label="Game over" sceneId={sceneId} isFirstInChapter={isFirstInChapter} />
        <InlineGameOverEditor
          item={gameOver}
          scenario={scenario}
          sceneId={sceneId}
          editMode={editMode}
          onChange={(next) => onChange(flatIndex, next)}
        />
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'recordCard') {
    const recordCard = item as RecordCardScene;
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slGameOver}`}>
        {controls}
        <BlockTypeLabel
          label="Fiche d'arrestation"
          sceneId={sceneId}
          isFirstInChapter={isFirstInChapter}
        />
        <InlineRecordCardEditor
          item={recordCard}
          editMode={editMode}
          onChange={(next) => onChange(flatIndex, next)}
        />
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'situationSortGame') {
    const sortGame = item as SituationSortGameScene;
    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slMinigame}`}>
        {controls}
        <BlockTypeLabel
          label="Jeu de tri"
          sceneId={sceneId}
          isFirstInChapter={isFirstInChapter}
        />
        <InlineSituationSortGameEditor
          item={sortGame}
          sceneId={sceneId}
          characters={characters}
          editMode={editMode}
          sortCategoryActions={sortCategoryActions}
          onChange={(next) => onChange(flatIndex, { ...next, id: sceneId })}
        />
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'observation') {
    const obs = item as ObservationScene;
    const objectCount = obs.elements?.length ?? 0;
    const anomalyCount = obs.elements?.filter((e) => (e.pedaShields ?? 0) > 0).length ?? 0;

    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slInspection}`}>
        {controls}
        <BlockTypeLabel label="Observation" sceneId={sceneId} isFirstInChapter={isFirstInChapter} />
        {obs.instruction && (
          <span className={styles.slText}>{obs.instruction}</span>
        )}
        <span className={styles.slText} style={{ color: '#9aa0a6', fontSize: '0.8rem' }}>
          {objectCount} objet{objectCount !== 1 ? 's' : ''} · {anomalyCount} anomalie{anomalyCount !== 1 ? 's' : ''}
        </span>
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  if (item.type === 'clickableScreen') {
    const screen = item as ClickableScreenScene;
    const zoneCount = screen.zones?.length ?? 0;

    return wrapBlock(
      blockId,
      editMode,
      <div className={`${styles.sl} ${styles.slInspection}`}>
        {controls}
        <BlockTypeLabel label="Écran cliquable" sceneId={sceneId} isFirstInChapter={isFirstInChapter} />
        {screen.instruction && (
          <span className={styles.slText}>{screen.instruction}</span>
        )}
        <span className={styles.slText} style={{ color: '#9aa0a6', fontSize: '0.8rem' }}>
          {zoneCount} zone{zoneCount !== 1 ? 's' : ''}
        </span>
      </div>,
      bgControls,
      inBackgroundSection,
      sceneId,
      overlayContext
    );
  }

  return wrapBlock(
    blockId,
    editMode,
    <div className={`${styles.sl} ${styles.slBlock}`}>
      {controls}
      <BlockTypeLabel
        label={getContentItemLabel(item)}
        sceneId={sceneId}
        isFirstInChapter={isFirstInChapter}
      />
      <span className={styles.slText}>{getContentItemSummary(item)}</span>
    </div>,
    bgControls,
    inBackgroundSection,
    overlayContext
  );
};
